import React, { useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconActivity,
  IconBarbell,
  IconBoltOff,
  IconCalendar,
  IconCircleFilled,
  IconEye,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import Menu, { MenuItemProps } from 'components/Menu';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useDeactivateDemoGym from 'hooks/demo-gym/useDeactivateDemoGym';
import useDemoGyms from 'hooks/demo-gym/useDemoGyms';
import useReactivateDemoGym from 'hooks/demo-gym/useReactivateDemoGym';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { DemoGymList } from 'types/generated';
import notificationHandler from 'utils/notifications';

import DeleteDemoGymModal from './DeleteDemoGymModal';
import DemoGymExpirationBadge from './DemoGymExpirationBadge';
import DemoGymTableEmptyState from './DemoGymTableEmptyState';
import { SearchFilters } from './types';

const DemoGymTableContainer = ({ searchFilters }: { searchFilters: SearchFilters }): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { session, impersonate } = useAuth();

  const [tableData, setTableData] = useState<DemoGymList[]>([]);
  const [selectedDemoGym, setSelectedDemoGym] = useState<DemoGymList | null>(null);
  const [isDeleteModalOpened, { toggle: toggleIsDeleteModalOpened }] = useDisclosure(false);

  const handleCloseDeleteModal = (): void => {
    toggleIsDeleteModalOpened();
    setSelectedDemoGym(null);
  };

  const { data, fetchNextPage, hasNextPage, isError, isSuccess, isLoading, isFetchingNextPage } =
    useDemoGyms({
      search: searchFilters.search,
      type: searchFilters.type,
      createdBy: session?.kiloStaff?.isSalesTeam ? session?.kiloStaff?.kiloStaffId : undefined,
    });

  const deactivateDemoGym = useDeactivateDemoGym();
  const reactivateDemoGym = useReactivateDemoGym();

  const handleDeactivateDemoGym = (demoGymId: string): void => {
    deactivateDemoGym.mutate(demoGymId, {
      onSuccess: () => {
        successNotification('Demo gym deactivated successfully');
      },
      onError: error => {
        errorNotification('Failed to deactivate demo gym:', error?.response?.data?.message);
      },
    });
  };

  const handleReactivateDemoGym = (demoGymId: string): void => {
    reactivateDemoGym.mutate(demoGymId, {
      onSuccess: () => {
        successNotification('Demo gym reactivated successfully');
      },
      onError: error => {
        errorNotification('Failed to reactivate demo gym:', error?.response?.data?.message);
      },
    });
  };

  const getLastLoggedInCell = (lastLoggedIn: string | null): JSX.Element => {
    switch (!!lastLoggedIn) {
      case true:
        return (
          <Text size='sm' color='dimmed'>
            {dayjs(lastLoggedIn).format('MMM D, h:mm A')}
          </Text>
        );

      default:
        return (
          <Text size='sm' color='dimmed'>
            Never Logged In
          </Text>
        );
    }
  };

  const getActionsColumnMenuItems = (demoGym: DemoGymList): MenuItemProps[] => {
    let items: MenuItemProps[] = [
      {
        icon: <IconEye size={theme.fontSizes.lg} />,
        title: 'Impersonate Owner',
        onClick: () => impersonate(demoGym.gym.owner.profile),
      },
      {
        icon: <IconUsers size={theme.fontSizes.lg} />,
        title: 'View Staff',
        onClick: () => navigate(`/gyms/${demoGym.gym.gymId}/staff`),
      },
      {
        icon: <IconBarbell size={theme.fontSizes.lg} />,
        title: 'View Athletes',
        onClick: () => navigate(`/gyms/${demoGym.gym.gymId}/athletes`),
      },
    ];

    if (demoGym.isActive) {
      items.push({
        color: 'orange',
        icon: <IconBoltOff size={theme.fontSizes.lg} color={theme.colors.orange[5]} />,
        title: 'Deactivate',
        onClick: () => handleDeactivateDemoGym(demoGym.demoGymId),
      });
    } else {
      items.push({
        color: 'green',
        icon: <IconActivity size={theme.fontSizes.lg} color={theme.colors.green[5]} />,
        title: 'Reactivate',
        onClick: () => handleReactivateDemoGym(demoGym.demoGymId),
      });
    }

    items.push({
      color: 'red',
      icon: <IconTrash size={theme.fontSizes.lg} color={theme.colors.red[5]} />,
      title: 'Delete',
      onClick: () => {
        toggleIsDeleteModalOpened();
        setSelectedDemoGym(demoGym);
      },
    });

    return items;
  };

  const columns = [
    {
      Header: 'Name',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Group>
          <Text truncate='end' maw={200}>
            {row.original.gym.title}
          </Text>
          {row.original.expiresAt && <DemoGymExpirationBadge expiresAt={row.original.expiresAt} />}
        </Group>
      ),
    },
    {
      Header: 'Gym Id',
      accessor: 'gym.gymId',
    },
    {
      Header: 'Owner Name',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Text truncate='end' maw={200}>
          {row.original.gym.owner.firstName} {row.original.gym.owner.lastName}
        </Text>
      ),
    },
    {
      Header: 'Owner Email',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Text truncate='end' maw={200}>
          {row.original.gym.owner.email}
        </Text>
      ),
    },
    {
      Header: 'Last Login',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Group spacing={8}>
          {row.original.isActive && (
            <IconCircleFilled
              size={15}
              style={{
                color: row.original.gym.owner.lastLogin ? theme.colors.green[5] : 'gray',
              }}
            />
          )}
          {getLastLoggedInCell(row.original.gym.owner.lastLogin)}
        </Group>
      ),
    },
    {
      Header: 'Status',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Group spacing={8}>
          <IconCircleFilled
            size={15}
            style={{ color: row.original.isActive ? theme.colors.kiloBlue[5] : theme.colors.red[5] }}
          />
          {row.original.expiresAt && (
            <Group spacing={4} align='center'>
              <IconCalendar size={18} color={theme.colors.gray[5]} />
              <Text size='sm' color='dimmed'>
                {dayjs(row.original.expiresAt).format('MMM D')}
              </Text>
            </Group>
          )}
        </Group>
      ),
    },
    {
      id: 'actions',
      Header: 'Actions',
      Cell: ({ row }: CellProps<DemoGymList>) => (
        <Group position='right'>
          <Menu items={getActionsColumnMenuItems(row.original)} />
        </Group>
      ),
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        EmptyState={<DemoGymTableEmptyState />}
      />

      {selectedDemoGym && (
        <DeleteDemoGymModal
          opened={isDeleteModalOpened}
          handleClose={handleCloseDeleteModal}
          demoGym={selectedDemoGym}
        />
      )}
    </>
  );
};

export default DemoGymTableContainer;
